import API, { alphaPath, responseWrapper } from './config'

import type { Memory, MemoryFields } from '@/types/memory.type'
import {
  BulkFolderMembershipParams,
  BulkFolderMembershipResult,
  Conversation,
  ConversationFolder,
  CreateConversationFolderParams,
  GenerateConversationNameParams,
  SendConversationSlackThreadParams,
  UpdateConversationFolderParams,
  UpdateConversationParams,
} from '@/types/conversation.type'
import {
  ArtifactShareListResponse,
  ShareArtifactPayload,
  UnshareArtifactPayload,
  ArtifactLibraryItem,
} from '@/types/artifactShare.type'
import {
  CreateMessageEvaluationParams,
  UpdateMessageEvaluationParams,
  MessageEvaluation,
} from '@/types/message_evaluation.type'
import {
  AdditionalUserFormData,
  ChangePasswordParams,
  CheckSsoFormData,
  CognitoUserFormData,
  CreateUserFormData,
  ForgotPasswordFormData,
  GoogleUserFormData,
  ResetPasswordParams,
  SetInitialPasswordParams,
  SignInFormData,
  UpdateUserParams,
  UpdateUserRoleAccessParams,
  User,
  UserRoleAccessResponse,
  VerifyEmailParams,
  VerifyInvitationTokenParams,
  VerifyProfileAccessTokenParams,
  VerifyResetPasswordParams,
} from '@/types/user.type'
import { UpsertUserPreferenceParams, UserPreference } from '@/types/user_preference.type'
import { ClientErrorReport, CreateClientErrorReportParams } from '@/types/client_error_report.type'
import { InvestmentProfile, UpsertInvestmentProfileParams } from '@/types/investment_profile.type'
import {
  CreateMessageParams,
  InitMessageParams,
  Message,
  MessageStatus,
  RegenerateMessageParams,
  TfsPrimerParams,
  TfsPulseCheckParams,
  UpdateMessageFollowUpQuestionsParams,
  UpdateMessageParams,
} from '@/types/message.type'
import {
  CreateReferenceParams,
  IntegrateSearchReferencesParams,
  Reference,
  SearchReferencesParams,
} from '@/types/reference.type'
import { CreateSubscriptionParams, BulkUpdateSubscriptionParams } from '@/types/subscription.type'
import {
  CreateUserSettingParams,
  UpdateUserSettingParams,
  UserSetting,
} from '@/types/user_setting.type'
import {
  CallSummarizerParams,
  ScreenerParams,
  ScreenerQuestionParams,
  TrackOwnershipParams,
  CatalystParams,
  UpdateScreenerParams,
  CompanyPageParams,
} from '@/types/workflow.type'
import { CreateSourceParams, Source } from '@/types/source'
import {
  OrganizationTeamJoinParams,
  TeamJoinResponse,
  UpdateOrganizationParams,
} from '@/types/organization.type'
import { CreateInvitationParams } from '@/types/invitation.type'
import { CatalystSearch, CreateCatalystSearchParams } from '@/types/catalyst_search.type'
import { Index, IndexType } from '@/types/index.type'
import { RmsIndividualSummaryParams, SearchRmsReferencesParams } from '@/types/rms_reference.type'
import { TableColumn } from '@/types/table.type'
import {
  CreateTopicParams,
  CreateTopicTrendsParams,
  Topic,
  TrendStockCamparisonParams,
  TrendStockComparison,
} from '@/types/trend.type'
import {
  MeetingInformation,
  VerifyUpdateMeetingTokenParams,
  UpdatePublicMeetingParams,
  CancelMeetingParams,
} from '@/types/meeting_information.type'
import { ComplianceMeetingSummary, ComplianceReviewAction } from '@/types/compliance.type'
import {
  CreateInferenceParams,
  CreateInferenceReferenceParams,
  Inference,
  InferenceReference,
} from '@/types/inference.type'
import {
  RmsV2ProcessThinkParams,
  RmsV2ProcessSubQuestionsParams,
  RmsV2ProcessSearchResultParams,
  RmsV2CreateAssistantMessageParams,
  RmsV2ProcessExternalSourceParams,
  FileExtensionParams,
  CreateStorageCollectionParams,
  StorageCollection,
  UpdateStorageCollectionParams,
} from '@/types/rms_v2.type'
import {
  BulkCreateFunctionCallParams,
  CreateFunctionCallParams,
  FunctionCall,
} from '@/types/function_call.type'
import { DocumentReference, DocumentReferenceParams } from '@/types/document_reference.type'
import {
  BriefScheduleCreateParams,
  BriefScheduleResponse,
  BriefScheduleUpdateParams,
  BriefingFeedbackItem,
  BriefingFeedbackSubmitParams,
  CalculateNextDeliveryParams,
  CalculateNextDeliveryResponse,
} from '@/types/briefing_agent.type'
import { ApiKeyCreateResponse, CreateApiKeyParams } from '@/types/api_key.type'
import {
  WorkspaceSkill,
  CreateWorkspaceSkillParams,
  DraftWorkspaceSkillParams,
  WorkspaceSkillDraft,
  UpdateWorkspaceSkillParams,
} from '@/types/workspace_skill.type'
import {
  SkillGroupResponse,
  SkillGroupsResponse,
  CreateSkillGroupParams,
  UpdateSkillGroupParams,
  SkillGroupSkillsParams,
} from '@/types/skill_group.type'
import {
  FactsetOauthStartParams,
  FactsetOauthStartResponse,
  FactsetOauthStatus,
} from '@/types/factset_oauth.type'
import {
  MicrosoftConnector,
  MicrosoftOauthStartParams,
  MicrosoftOauthStartResponse,
  MicrosoftOauthStatus,
} from '@/types/microsoft_oauth.type'
import { Trigger, TriggerCreateParams, TriggerUpdateParams } from '@/types/alert_agent.type'
import {
  SlackInstallStartParams,
  SlackOauthStartParams,
  SlackOauthStartResponse,
  SlackOauthStatus,
} from '@/types/slack_oauth.type'
import {
  CreateOntologyEntityParams,
  OntologyEntity,
  UpdateOntologyEntityParams,
} from '@/types/ontology.type'
import type {
  WebWatchCreateParams,
  WebWatchDeleteResponse,
  WebWatchMutationResponse,
  WebWatchUpdateParams,
} from '@/types/web_watch.type'

export const signInUser = () => async (params: SignInFormData) =>
  API.post(`${alphaPath}/users/sign_in`, { user: params })

export const signUpUser = (invitation_token?: string) => async (params: CreateUserFormData) => {
  return await responseWrapper(() =>
    API.post(`${alphaPath}/users/sign_up`, { user: params, invitation_token })
  )
}

export const signOutUser = () => async () => {
  return await responseWrapper(() => API.post(`${alphaPath}/users/sign_out`))
}

// Artifact sharing — owner-only (agent-server enforces ownership; alpha stamps
// the sharer identity + intersects recipients with the org). Both return the
// current recipient list.
export const shareArtifact = (artifactId: string, payload: ShareArtifactPayload) => async () => {
  return await responseWrapper<ArtifactShareListResponse>(() =>
    API.post<ArtifactShareListResponse>(`${alphaPath}/artifacts/${artifactId}/shares`, payload)
  )
}

export const unshareArtifact =
  (artifactId: string, payload: UnshareArtifactPayload) => async () => {
    return await responseWrapper<ArtifactShareListResponse>(() =>
      API.delete<ArtifactShareListResponse>(`${alphaPath}/artifacts/${artifactId}/shares`, {
        data: payload,
      })
    )
  }

// Memory page writes — alpha-server authors every one as a user edit.
// `origin` records where the user wrote it (the page, or a chat's memory panel): every
// memory carries its provenance.
export const createMemory =
  (payload: MemoryFields, origin: 'page' | 'panel' = 'page') =>
  async () => {
    return await responseWrapper<Memory>(() =>
      API.post<Memory>(`${alphaPath}/users/me/memories`, { ...payload, origin })
    )
  }

export const updateMemory = (memoryId: string, payload: Partial<MemoryFields>) => async () => {
  return await responseWrapper<Memory>(() =>
    API.patch<Memory>(`${alphaPath}/users/me/memories/${memoryId}`, payload)
  )
}

export const deleteMemory = (memoryId: string) => async () => {
  return await responseWrapper<Memory>(() =>
    API.delete<Memory>(`${alphaPath}/users/me/memories/${memoryId}`)
  )
}

export const sendResetPasswordEmail = () => (params: ForgotPasswordFormData) =>
  API.post(`${alphaPath}/users/forgot_password`, { user: params })

export const verifyResetPasswordToken = () => (params: VerifyResetPasswordParams) =>
  API.post(`${alphaPath}/users/verify_reset_password`, { user: params })

export const resetPassword = () => (params: ResetPasswordParams) =>
  API.patch(`${alphaPath}/users/reset_password`, { user: params })

export const changePassword = () => (params: ChangePasswordParams) =>
  API.patch(`${alphaPath}/users/change_password`, { user: params })

export const setInitialPassword = () => async (params: SetInitialPasswordParams) => {
  return await responseWrapper<User>(() =>
    API.patch<User>(`${alphaPath}/users/set_initial_password`, { user: params })
  )
}

export const verifyEmailToken = () => async (params: VerifyEmailParams) => {
  return await responseWrapper<User>(() =>
    API.post<User>(`${alphaPath}/users/verify_email`, { user: params })
  )
}

export const verifyOrganizationEmailToken = () => async (params: VerifyEmailParams) =>
  API.post(`${alphaPath}/users/verify_organization_email`, { user: params })

export const googleUser = () => (params: GoogleUserFormData) =>
  API.post(`${alphaPath}/users/oauth/google`, { ...params })

export const cognitoUser = () => (params: CognitoUserFormData) =>
  API.post(`${alphaPath}/users/oauth/cognito`, { ...params })

export const updateUser = () => async (params: UpdateUserParams) => {
  return await responseWrapper<User>(() =>
    API.patch<User>(`${alphaPath}/users/${params.id}`, { ...params })
  )
}

export const checkSsoUser = () => async (params: CheckSsoFormData) =>
  API.post<{ signin_url: string }>(`${alphaPath}/users/check_sso`, params)

export const updateUserAdditionalInfo = () => async (params: AdditionalUserFormData) => {
  return await responseWrapper<User>(() =>
    API.patch<User>(`${alphaPath}/users/additional_info`, params)
  )
}

export const createUserSetting = () => async (params: CreateUserSettingParams) => {
  return await responseWrapper<UserSetting>(() =>
    API.post<UserSetting>(`${alphaPath}/user_settings`, { ...params })
  )
}

export const updateUserSetting = () => async (params: UpdateUserSettingParams) => {
  return await responseWrapper<UserSetting>(() =>
    API.patch<UserSetting>(`${alphaPath}/user_settings/${params.id}`, { ...params })
  )
}

export const deleteUserSetting = () => async (id: string) => {
  return await responseWrapper<null>(() => API.delete<null>(`${alphaPath}/user_settings/${id}`))
}

export const postBannerShown = () => async () => {
  return await responseWrapper<void>(() =>
    API.post<void>(`${alphaPath}/behavioral_profiling/banner_shown`)
  )
}

export const upsertInvestmentProfile = () => async (params: UpsertInvestmentProfileParams) => {
  return await responseWrapper<InvestmentProfile>(() =>
    API.put<InvestmentProfile>(`${alphaPath}/investment_profile/upsert`, { ...params })
  )
}

export const upsertUserPreference = () => async (params: UpsertUserPreferenceParams) => {
  return await responseWrapper<UserPreference>(() =>
    API.put<UserPreference>(`${alphaPath}/user_preference/upsert`, { ...params })
  )
}

export const createConversation = () => async (params: InitMessageParams) => {
  return await responseWrapper<Conversation>(
    () => API.post<Conversation>(`${alphaPath}/conversations`, { ...params }),
    false
  )
}

export const updateConversation = () => async (params: UpdateConversationParams) => {
  return await responseWrapper<Conversation>(() =>
    API.patch<Conversation>(`${alphaPath}/conversations/${params.id}`, { ...params })
  )
}

export const destroyConversation = () => async (id: string) => {
  return await responseWrapper<Conversation>(() =>
    API.delete<Conversation>(`${alphaPath}/conversations/${id}`)
  )
}

/**
 * Idempotent delete for a per-tab temp conversation (Analytics opt-in).
 * The server only deletes rows scoped to (user, client_temp_id, status: temp)
 * so calling this with a stale or unknown id is a safe no-op.
 *
 * Intended primarily for pagehide (tab close) where we want the request to
 * fire-and-forget. Prefer `navigator.sendBeacon` at the call site for that
 * lifecycle; this helper is for foreground cases (auth change, submit
 * success) where we can await the response.
 */
export const destroyTempConversation = async (client_temp_id: string) => {
  return await responseWrapper<null>(() =>
    API.delete<null>(`${alphaPath}/conversations/destroy_temp`, {
      params: { client_temp_id },
    })
  )
}

export const destroyConversationWithSources = () => async (id: string) => {
  return await responseWrapper<Conversation>(() =>
    API.post<Conversation>(`${alphaPath}/conversations/${id}/destroy_with_sources`)
  )
}

// --- Conversation folders (v1.3) ---

export const createConversationFolder = () => async (params: CreateConversationFolderParams) => {
  return await responseWrapper<ConversationFolder>(() =>
    API.post<ConversationFolder>(`${alphaPath}/conversation_folders`, { ...params })
  )
}

export const updateConversationFolder = () => async (params: UpdateConversationFolderParams) => {
  return await responseWrapper<ConversationFolder>(() =>
    API.patch<ConversationFolder>(`${alphaPath}/conversation_folders/${params.id}`, { ...params })
  )
}

export const destroyConversationFolder = () => async (id: string) => {
  return await responseWrapper<void>(() =>
    API.delete<void>(`${alphaPath}/conversation_folders/${id}`)
  )
}

export const bulkFolderMembership = () => async (params: BulkFolderMembershipParams) => {
  const { folderId, conversation_ids, op } = params
  return await responseWrapper<BulkFolderMembershipResult>(() =>
    API.post<BulkFolderMembershipResult>(
      `${alphaPath}/conversation_folders/${folderId}/conversations/bulk`,
      { conversation_ids, op }
    )
  )
}

export const generateConversationName =
  () =>
  async ({
    conversation_id,
    params,
  }: {
    conversation_id: string
    params: GenerateConversationNameParams
  }) => {
    return await responseWrapper<string>(() =>
      API.post<string>(`${alphaPath}/conversations/${conversation_id}/generate_name`, {
        ...params,
      })
    )
  }

export const createMessage =
  () =>
  async ({ conversation_id, params }: { conversation_id: string; params: CreateMessageParams }) => {
    return await responseWrapper<Message>(
      () =>
        API.post<Message>(
          `${alphaPath}/conversations/${conversation_id}/messages`,
          { message: params },
          // Server-side latency over 7d/6.1k requests: p50 234ms, p99 717ms,
          // worst 3.0s — so anything past a few seconds is the network, not
          // the backend. Inheriting the instance-wide 300s means a request
          // that landed on a dead HTTP/2 connection hangs silently for five
          // minutes (Sentry: "timeout of 300000ms exceeded") while the UI
          // looks completely idle. Failing fast also tears that dead
          // connection down, so the user's resend goes out on a fresh one.
          // Not lower than this because `content` can carry concatenated
          // pasted bodies (see buildPastedQuery), which need upload headroom
          // on a slow uplink — cutting those short would push users into
          // resending and creating duplicates.
          { timeout: 15000 }
        ),
      false
    )
  }

export const updateMessageStatus =
  () =>
  async ({ message_id, status }: { message_id: string; status: MessageStatus }) => {
    return await responseWrapper<Message>(() =>
      API.patch<Message>(`${alphaPath}/messages/${message_id}/status`, {
        id: message_id,
        status,
      })
    )
  }

export const updateMessageUserLeft = () => async (message_id: string) => {
  return await responseWrapper(() =>
    API.post(`${alphaPath}/messages/${message_id}/notify_user_left`)
  )
}

export const updateMessageFollowUpQuestions =
  () => async (params: UpdateMessageFollowUpQuestionsParams) => {
    return await responseWrapper<Message>(() =>
      API.patch<Message>(`${alphaPath}/messages/${params.id}/update_follow_up_questions`, {
        ...params,
      })
    )
  }

export const regenerateMessage = () => async (params: RegenerateMessageParams) => {
  return await responseWrapper<Message>(() =>
    API.patch<Message>(`${alphaPath}/messages/${params.id}/regenerate`, {
      ...params,
    })
  )
}

export const updateMessage = () => async (params: UpdateMessageParams) => {
  return await responseWrapper<Message>(() =>
    API.patch<Message>(`${alphaPath}/messages/${params.id}`, {
      onyx_chat_message_id: params.onyx_chat_message_id,
    })
  )
}

export const createMessageEvaluation = () => async (params: CreateMessageEvaluationParams) => {
  return await responseWrapper<MessageEvaluation>(() =>
    API.post<MessageEvaluation>(`${alphaPath}/messages/${params.message_id}/message_evaluations`, {
      evaluation: params.evaluation,
      feedback: params.feedback,
    })
  )
}

export const createClientErrorReport = () => async (params: CreateClientErrorReportParams) => {
  // The report dialog shows its own inline error (429 included), so no global toast.
  return await responseWrapper<ClientErrorReport>(
    () => API.post<ClientErrorReport>(`${alphaPath}/client_error_reports`, params),
    false
  )
}

export const updateMessageEvaluation = () => async (params: UpdateMessageEvaluationParams) => {
  return await responseWrapper<MessageEvaluation>(() =>
    API.patch<MessageEvaluation>(`${alphaPath}/message_evaluations/${params.id}`, {
      ...params,
    })
  )
}

export const searchReferences = () => async (params: SearchReferencesParams) => {
  return await responseWrapper<string>(() =>
    API.post<string>(`${alphaPath}/references/search`, {
      ...params,
    })
  )
}

export const renewalSearchReferences = () => async (params: SearchReferencesParams) => {
  return await responseWrapper<string>(() =>
    API.post<string>(`${alphaPath}/references/renewal_search`, {
      ...params,
    })
  )
}

export const integrateSearch = () => async (params: IntegrateSearchReferencesParams) => {
  return await responseWrapper<Message>(() =>
    API.post<Message>(`${alphaPath}/references/integrate_search_v2`, {
      ...params,
    })
  )
}

export const createReference = () => async (params: CreateReferenceParams) => {
  return await responseWrapper<Reference[]>(() =>
    API.post<Reference[]>(`${alphaPath}/messages/${params.message_id}/references`, {
      ...params,
    })
  )
}

export const searchRmsReferences = () => async (params: SearchRmsReferencesParams) => {
  return await responseWrapper<string>(() =>
    API.post<string>(`${alphaPath}/rms_references/search`, {
      ...params,
    })
  )
}

export const createSubscription = () => async (params: CreateSubscriptionParams) => {
  return await responseWrapper<string[]>(() =>
    API.post<string[]>(`${alphaPath}/subscriptions`, {
      ...params,
    })
  )
}

export const bulkUpdateSubscription = () => async (params: BulkUpdateSubscriptionParams) => {
  return await responseWrapper<boolean>(() =>
    API.patch<boolean>(`${alphaPath}/subscriptions/bulk_update`, {
      ...params,
    })
  )
}

export const updateOrganization = () => async (params: UpdateOrganizationParams) => {
  return await responseWrapper<User>(() =>
    API.patch<User>(`${alphaPath}/organizations/${params.id}`, { ...params })
  )
}

export const organizationTeamJoin = () => async (params: OrganizationTeamJoinParams) => {
  return await responseWrapper<TeamJoinResponse>(() =>
    API.post<TeamJoinResponse>(`${alphaPath}/organizations/team_join`, { ...params })
  )
}

export const userRoleAccess = () => async (params: UpdateUserRoleAccessParams) => {
  return await responseWrapper<UserRoleAccessResponse>(() =>
    API.patch<UserRoleAccessResponse>(`${alphaPath}/users/role_access`, { ...params })
  )
}

export const removeUser = () => async (id: string) => {
  return await responseWrapper<User>(() => API.delete<User>(`${alphaPath}/users/${id}`))
}

export const createInvitations = () => async (params: CreateInvitationParams) => {
  return await responseWrapper<boolean>(() =>
    API.post<boolean>(`${alphaPath}/invitations`, { ...params })
  )
}

export const destroyInvitation = () => async (id: string) => {
  return await responseWrapper<void>(() => API.delete<void>(`${alphaPath}/invitations/${id}`))
}

export const resendInvitation = () => async (id: string) => {
  return await responseWrapper<void>(() => API.post<void>(`${alphaPath}/invitations/${id}/resend`))
}

export const verifyInvitationToken = () => async (params: VerifyInvitationTokenParams) => {
  return await responseWrapper(() => API.post(`${alphaPath}/invitations/verify`, { ...params }))
}

export const createSource = () => async (params: CreateSourceParams) => {
  return await responseWrapper<Source>(() =>
    API.post<Source>(`${alphaPath}/sources`, {
      ...params,
    })
  )
}

export const destroySource = () => async (id: string) => {
  return await responseWrapper<Source>(() => API.delete<Source>(`${alphaPath}/sources/${id}`))
}

export const bulkDeleteSources = (conversationId?: string) => async (ids: string[]) => {
  return await responseWrapper(() =>
    API.delete(`${alphaPath}/conversations/${conversationId}/sources/bulk`, {
      params: {
        ids,
      },
    })
  )
}

export const createCatalystSearch = () => async (params: CreateCatalystSearchParams) => {
  return await responseWrapper<CatalystSearch>(() =>
    API.post<CatalystSearch>(`${alphaPath}/catalyst_searches`, {
      ...params,
    })
  )
}

export const createIndex = () => async (indexType?: IndexType) => {
  return await responseWrapper<Index>(() =>
    API.post<Index>(`${alphaPath}/indices`, {
      index_type: indexType,
    })
  )
}

export const executeScreener = () => async (params: ScreenerParams) => {
  return await responseWrapper<{ message: Message; conversation: Conversation }>(() =>
    API.post<{ message: Message; conversation: Conversation }>(
      `${alphaPath}/alpha_workflows/execute_screener`,
      { ...params }
    )
  )
}

export const executeScreenerQuestion = () => async (params: ScreenerQuestionParams) => {
  return await responseWrapper<TableColumn[]>(() =>
    API.post<TableColumn[]>(`${alphaPath}/alpha_workflows/screener_question`, { ...params })
  )
}

export const updateScreener = () => async (params: UpdateScreenerParams) => {
  return await responseWrapper<Message>(() =>
    API.post<Message>(`${alphaPath}/alpha_workflows/update_screener`, { ...params })
  )
}

export const createOrFindTopic = () => async (params: CreateTopicParams) => {
  return await responseWrapper<Topic>(() => API.post<Topic>(`${alphaPath}/topics`, { ...params }))
}

export const createTopicTrends = () => async (params: CreateTopicTrendsParams) => {
  return await responseWrapper<Topic>(() =>
    API.post<Topic>(`${alphaPath}/topics/${params.topic_id}/topic_trends`, { ...params })
  )
}

export const createCallSummarize = () => async (params: CallSummarizerParams) => {
  return await responseWrapper<{ conversation: Conversation; message: Message }>(() =>
    API.post<{ conversation: Conversation; message: Message }>(
      `${alphaPath}/messages/create_call_summarize`,
      { ...params }
    )
  )
}

export const executeCallSummarize = () => async (params: CallSummarizerParams) => {
  return await responseWrapper<Message>(() =>
    API.post<Message>(`${alphaPath}/messages/execute_call_summarize`, { ...params })
  )
}

export const executeOwnershipData = () => async (params: TrackOwnershipParams) => {
  return await responseWrapper<{ conversation: Conversation; message: Message }>(() =>
    API.post<{ conversation: Conversation; message: Message }>(
      `${alphaPath}/messages/execute_ownership_data`,
      {
        ...params,
        per: 1000,
        order_key: 'position',
        transaction_order_key: 'transaction_date',
        order_direction: 'desc',
      }
    )
  )
}

export const executeCatalystData = () => async (params: CatalystParams) => {
  return await responseWrapper<{ message: Message; conversation: Conversation }>(() =>
    API.post<{ message: Message; conversation: Conversation }>(
      `${alphaPath}/alpha_workflows/execute_catalyst`,
      {
        ...params,
      }
    )
  )
}

export const executeOwnershipTransactionData = () => async (params: TrackOwnershipParams) => {
  return await responseWrapper<{ conversation: Conversation; message: Message }>(() =>
    API.post<{ conversation: Conversation; message: Message }>(
      `${alphaPath}/messages/execute_ownership_transactions_data`,
      {
        ...params,
        per: 1000,
        order_key: 'position',
        transaction_order_key: 'transaction_date',
        order_direction: 'desc',
      }
    )
  )
}

export const executeCompanyPage = () => async (params: CompanyPageParams) => {
  return await responseWrapper<{
    message: Message
    conversation: Conversation
    needs_caching: boolean
    latest_fy: number
    latest_fq: number
  }>(() =>
    API.post<{
      message: Message
      conversation: Conversation
      needs_caching: boolean
      latest_fy: number
      latest_fq: number
    }>(`${alphaPath}/agent_v2/execute_company_page`, { ...params })
  )
}

export const executeBulkEarningsHistory =
  () =>
  async ({ message_id, lang }: { message_id: string; lang?: string }) => {
    return await responseWrapper<{ success: boolean }>(() =>
      API.post<{ success: boolean }>(`${alphaPath}/agent_v2/bulk_earnings_history`, {
        message_id,
        ...(lang && { lang }),
      })
    )
  }

export const executeForceUpdate =
  () =>
  async ({
    message_id,
    target_tabs,
    lang,
  }: {
    message_id: string
    target_tabs?: string[]
    lang?: string
  }) => {
    return await responseWrapper<{ success: boolean }>(() =>
      API.post<{ success: boolean }>(`${alphaPath}/agent_v2/force_update_company_page`, {
        message_id,
        target_tabs: target_tabs || [],
        ...(lang && { lang }),
      })
    )
  }

export const SendProfileAccessEmail = () => async () => {
  return await responseWrapper(() => API.post(`${alphaPath}/users/send_profile_access_email`))
}

export const VerifyProfileAccessToken = () => async (params: VerifyProfileAccessTokenParams) => {
  return await responseWrapper<User>(() =>
    API.post<User>(`${alphaPath}/users/verify_profile_access_token`, { ...params })
  )
}

export const VerifyUpdateMeetingToken = () => async (params: VerifyUpdateMeetingTokenParams) => {
  return await responseWrapper<MeetingInformation>(() =>
    API.post<MeetingInformation>(
      `${alphaPath}/meeting_informations/${params.id}/verify_update_token`,
      {
        ...params,
      }
    )
  )
}

export const PublicUpdateMeeting = () => async (params: UpdatePublicMeetingParams) => {
  return await responseWrapper<MeetingInformation>(() =>
    API.patch<MeetingInformation>(`${alphaPath}/meeting_informations/${params.id}/public`, {
      ...params,
    })
  )
}

export const CancelMeeting = () => async (params: CancelMeetingParams) => {
  return await responseWrapper<MeetingInformation>(
    () =>
      API.post<MeetingInformation>(
        `${alphaPath}/meeting_informations/${params.id}/cancel`,
        params.reason === undefined ? undefined : { reason: params.reason }
      ),
    false
  )
}

// MEA-18: a compliance viewer approves or rejects a meeting held for review. 409 when the meeting
// is no longer waiting for a decision. Errors are left to the caller, which shows its own toast.
export const DecideComplianceMeetingReview =
  (action: ComplianceReviewAction) => async (params: { id: string }) => {
    return await responseWrapper<{ data: ComplianceMeetingSummary }>(
      () =>
        API.post<{ data: ComplianceMeetingSummary }>(
          `${alphaPath}/compliance/meeting_summaries/${params.id}/${action}`
        ),
      false
    )
  }

export const TrendStockCamparison = () => async (params: TrendStockCamparisonParams) => {
  return await responseWrapper<TrendStockComparison>(() =>
    API.post<TrendStockComparison>(
      `${alphaPath}/topics/${params.topic_id}/trend_stock_comparison`,
      {
        ...params,
      }
    )
  )
}

export const rmsIndividualSummary = () => async (params: RmsIndividualSummaryParams) => {
  return await responseWrapper<Message>(() =>
    API.post<Message>(`${alphaPath}/rms_references/individual_summary`, {
      ...params,
    })
  )
}

export const RmsV2ProcessThink = () => async (params: RmsV2ProcessThinkParams) => {
  return await responseWrapper<Inference>(
    () =>
      API.post<Inference>(`${alphaPath}/rms_v2/process_think`, {
        ...params,
      }),
    false
  )
}

export const RmsV2ProcessSubQuestions = () => async (params: RmsV2ProcessSubQuestionsParams) => {
  return await responseWrapper<FunctionCall>(
    () =>
      API.post<FunctionCall>(`${alphaPath}/rms_v2/process_sub_questions`, {
        ...params,
      }),
    false
  )
}

export const RmsV2ProcessInferredMetadata = () => async (params: any) => {
  return await responseWrapper<FunctionCall>(
    () =>
      API.post<FunctionCall>(`${alphaPath}/rms_v2/process_inferred_metadata`, {
        ...params,
      }),
    false
  )
}

export const RmsV2ProcessSearchResult = () => async (params: RmsV2ProcessSearchResultParams) => {
  return await responseWrapper<any>(
    () =>
      API.post<any>(`${alphaPath}/rms_v2/process_search_result`, {
        ...params,
      }),
    false
  )
}

export const RmsV2ProcessExternalSource =
  () => async (params: RmsV2ProcessExternalSourceParams) => {
    return await responseWrapper<any>(
      () =>
        API.post<any>(`${alphaPath}/rms_v2/process_external_source`, {
          ...params,
        }),
      false
    )
  }

export const RmsV2CreateAssistantMessage =
  () => async (params: RmsV2CreateAssistantMessageParams) => {
    return await responseWrapper<Message>(
      () =>
        API.post<Message>(`${alphaPath}/rms_v2/create_assistant_message`, {
          ...params,
        }),
      false
    )
  }

export const createDocumentReference = () => async (params: DocumentReferenceParams) => {
  return await responseWrapper<DocumentReference>(
    () =>
      API.post<DocumentReference>(
        `${alphaPath}/messages/${params.message_id}/document_references`,
        {
          ...params,
        }
      ),
    false
  )
}

export const createInference = () => async (params: CreateInferenceParams) => {
  return await responseWrapper<Inference>(() =>
    API.post<Inference>(`${alphaPath}/inferences`, { ...params })
  )
}

export const createFunctionCall = () => async (params: CreateFunctionCallParams) => {
  return await responseWrapper<FunctionCall>(
    () => API.post<FunctionCall>(`${alphaPath}/function_calls`, { ...params }),
    false
  )
}

export const bulkCreateFunctionCalls = () => async (params: BulkCreateFunctionCallParams) => {
  return await responseWrapper<FunctionCall[]>(
    () => API.post<FunctionCall[]>(`${alphaPath}/function_calls/bulk_create`, { ...params }),
    false
  )
}

export const createInferenceReference = () => async (params: CreateInferenceReferenceParams) => {
  return await responseWrapper<InferenceReference[]>(() =>
    API.post<InferenceReference[]>(`${alphaPath}/inference_references`, { ...params })
  )
}

export const executePrimer = () => async (params: TfsPrimerParams) => {
  return await responseWrapper<{ conversation: Conversation; message: Message }>(() =>
    API.post<{ conversation: Conversation; message: Message }>(
      `${alphaPath}/messages/execute_primer`,
      {
        ...params,
      }
    )
  )
}

export const executePulseCheck = () => async (params: TfsPulseCheckParams) => {
  return await responseWrapper<{ conversation: Conversation; message: Message }>(() =>
    API.post<{ conversation: Conversation; message: Message }>(
      `${alphaPath}/messages/execute_pulse_check`,
      {
        ...params,
      }
    )
  )
}

export const createBriefSchedule = (data: BriefScheduleCreateParams) => async () => {
  return await responseWrapper<BriefScheduleResponse>(() =>
    API.post<BriefScheduleResponse>(`${alphaPath}/brief_schedules`, data)
  )
}

export const updateBriefSchedule = () => async (params: BriefScheduleUpdateParams) => {
  return await responseWrapper<BriefScheduleResponse>(() =>
    API.patch<BriefScheduleResponse>(`${alphaPath}/brief_schedules/${params.id}`, { ...params })
  )
}

export const calculateNextDelivery = () => async (params: CalculateNextDeliveryParams) => {
  return await responseWrapper<CalculateNextDeliveryResponse>(() =>
    API.post<CalculateNextDeliveryResponse>(
      `${alphaPath}/brief_schedules/calculate_next_delivery`,
      params
    )
  )
}

export const bulkActivateBriefSchedule =
  () => async (params: { ids: string[]; is_active: boolean }) => {
    return await responseWrapper<BriefScheduleResponse[]>(() =>
      API.patch<BriefScheduleResponse[]>(`${alphaPath}/brief_schedules/bulk_activate`, {
        ...params,
      })
    )
  }

// Phase 1b of PRD #2500 — submit a section/range highlight feedback.
export const createBriefingFeedbackItem =
  (deliveryId: string) => async (data: BriefingFeedbackSubmitParams) => {
    return await responseWrapper<BriefingFeedbackItem>(() =>
      API.post<BriefingFeedbackItem>(
        `${alphaPath}/brief_deliveries/${deliveryId}/feedback_items`,
        data
      )
    )
  }

// Token-anonymous variant for the /brief_agent/feedback Next.js page.
// NOTE: this intentionally bypasses `responseWrapper` (which routes errors to
// `customToast`). Email recipients arriving via this page may not be
// authenticated to chat.linqalpha.com; the global toast theme assumes the
// authenticated app shell. The token landing page renders its own minimal
// error UI instead, and `useSubmitBriefingFeedback` attaches a localized
// `onError` for the 429/5xx user-feedback path.
export const createBriefingFeedbackItemAnonymous =
  (token: string) =>
  async (data: BriefingFeedbackSubmitParams): Promise<BriefingFeedbackItem> => {
    const response = await API.post<BriefingFeedbackItem>(
      `${alphaPath}/email_feedbacks/${encodeURIComponent(token)}/items`,
      data
    )
    return response.data
  }

export const deleteBriefSchedule = () => async (scheduleId: string) => {
  return await responseWrapper<void>(() =>
    API.delete<void>(`${alphaPath}/brief_schedules/${scheduleId}`)
  )
}

export const getFileExtension = () => async (params: FileExtensionParams) => {
  return await responseWrapper<any>(() =>
    API.get<any>(`${alphaPath}/rms_v2/extension`, {
      params: {
        ...params,
      },
    })
  )
}

export const sendConversationSlackThread =
  () => async (params: SendConversationSlackThreadParams) => {
    return await responseWrapper<any>(() =>
      API.post<any>(`${alphaPath}/conversations/${params.conversation_id}/slack_thread`, {
        ...params,
      })
    )
  }

// TODO: Temp Use Only for FIL QA
export const executeT2S = () => async (query: string) => {
  return await responseWrapper<any>(() => API.post<any>(`${alphaPath}/onyx/t2s`, { query }))
}

export const executeTrendV2 =
  () =>
  async (params: {
    conversation_id: string
    filters: {
      company: string
      topic: string
      research_focus: string
      date_range: 'week' | 'month'
    }
    start_time: string
    function_mode?: string
  }) => {
    return await responseWrapper<{ conversation: Conversation; message: Message }>(() =>
      API.post<{ conversation: Conversation; message: Message }>(
        `${alphaPath}/messages/execute_trend_v2`,
        { ...params, function_mode: params.function_mode || 'trend_v2' }
      )
    )
  }

// Storage Collections
export const createStorageCollection = () => async (params: CreateStorageCollectionParams) => {
  return await responseWrapper<StorageCollection>(() =>
    API.post<StorageCollection>(`${alphaPath}/storage_collections`, params)
  )
}

export const updateStorageCollection = () => async (params: UpdateStorageCollectionParams) => {
  const { id, ...updateData } = params
  return await responseWrapper<StorageCollection>(() =>
    API.patch<StorageCollection>(`${alphaPath}/storage_collections/${id}`, updateData)
  )
}

export const deleteStorageCollection = () => async (id: string) => {
  return await responseWrapper<{ success: boolean }>(() =>
    API.delete<{ success: boolean }>(`${alphaPath}/storage_collections/${id}`)
  )
}

export const completeOnboarding = () => async () => {
  return await responseWrapper<User>(() =>
    API.patch<User>(`${alphaPath}/users/update_earnings_management_onboarding`)
  )
}

// API Keys
export const createApiKey = () => async (params: CreateApiKeyParams) => {
  return await responseWrapper<ApiKeyCreateResponse>(() =>
    API.post<ApiKeyCreateResponse>(`${alphaPath}/api_keys`, { ...params })
  )
}

export const revokeApiKey = () => async (id: number) => {
  return await responseWrapper<void>(() => API.delete<void>(`${alphaPath}/api_keys/${id}`))
}

// Workspace Skills

export const createWorkspaceSkill = () => async (params: CreateWorkspaceSkillParams) => {
  return await responseWrapper<WorkspaceSkill>(() =>
    API.post<WorkspaceSkill>(`${alphaPath}/workspace_skills`, params)
  )
}

export const draftWorkspaceSkill = () => async (params: DraftWorkspaceSkillParams) => {
  const res = await API.post<{ data: WorkspaceSkillDraft }>(
    `${alphaPath}/workspace_skills/draft`,
    params
  )
  return res.data.data
}

export const updateWorkspaceSkill = () => async (params: UpdateWorkspaceSkillParams) => {
  const { id, ...updateData } = params
  return await responseWrapper<WorkspaceSkill>(() =>
    API.patch<WorkspaceSkill>(`${alphaPath}/workspace_skills/${id}`, updateData)
  )
}

// isAlert=false for the same reason as the moves below: an organization skill delete can be
// refused with a 422 the page shows verbatim.
export const deleteWorkspaceSkill = () => async (id: string) => {
  return await responseWrapper<{ success: boolean }>(
    () => API.delete<{ success: boolean }>(`${alphaPath}/workspace_skills/${id}`),
    false
  )
}

// Per-user Skill Library hide (COV-401). Changes only this user's library listing.
export const hideWorkspaceSkill = () => async (id: string) => {
  return await responseWrapper<{ success: boolean }>(() =>
    API.post<{ success: boolean }>(`${alphaPath}/workspace_skills/${id}/hide`)
  )
}

export const unhideWorkspaceSkill = () => async (id: string) => {
  return await responseWrapper<{ success: boolean }>(() =>
    API.post<{ success: boolean }>(`${alphaPath}/workspace_skills/${id}/unhide`)
  )
}

// Self-service personal -> organization move (COV-152 M5). Deliberately takes
// no body: the target organization is derived server-side from the caller.
export const moveWorkspaceSkillToOrganization = () => async (id: string) => {
  return await responseWrapper<WorkspaceSkill>(() =>
    API.post<WorkspaceSkill>(`${alphaPath}/workspace_skills/${id}/move_to_organization`)
  )
}

// The reverse: organization -> personal. Without recipientUserId the new owner is the
// caller (the member who created the skill); with it, an org admin returns the skill to the
// author admin_move_recipient named, and the server refuses if that changed since the page
// loaded. isAlert=false because the page toasts every failure itself, and responseWrapper
// alerts on any status outside 400-409, so a 422 refusal would also get "try again later".
export const moveWorkspaceSkillToPersonal =
  () =>
  async ({ id, recipientUserId }: { id: string; recipientUserId?: string }) => {
    return await responseWrapper<WorkspaceSkill>(
      () =>
        API.post<WorkspaceSkill>(
          `${alphaPath}/workspace_skills/${id}/move_to_personal`,
          recipientUserId ? { recipient_user_id: recipientUserId } : undefined
        ),
      false
    )
  }

// FactSet OAuth connector (PRD #2716) ----------------------------------------

// `start` returns the FactSet authorize URL + an opaque state. The client
// then navigates the user to `authorize_url`. FactSet redirects back to
// `redirect_uri` (a client route) with `?code=...&state=...`, which the
// client passes to the alpha-server callback via completeFactsetOauthCallback.
export const startFactsetOauth = () => async (params: FactsetOauthStartParams) => {
  return await responseWrapper<{ data: FactsetOauthStartResponse }>(() =>
    API.post<{ data: FactsetOauthStartResponse }>(`${alphaPath}/oauth/factset/start`, params)
  )
}

// Revokes the active FactSet OAuth credential for the current user.
// Returns the post-revoke status (`connected: false`).
export const disconnectFactsetOauth = () => async () => {
  return await responseWrapper<{ data: FactsetOauthStatus }>(() =>
    API.delete<{ data: FactsetOauthStatus }>(`${alphaPath}/oauth/factset`)
  )
}

// LIN-721 — Microsoft (Teams) connector, mirrors FactSet above. Returns the
// Microsoft authorize URL + opaque state; the client redirects the user to it,
// then hands the returned `?code=&state=` back via completeMicrosoftOauthCallback.
export const startMicrosoftOauth = () => async (params: MicrosoftOauthStartParams) => {
  return await responseWrapper<{ data: MicrosoftOauthStartResponse }>(() =>
    API.post<{ data: MicrosoftOauthStartResponse }>(`${alphaPath}/oauth/microsoft/start`, params)
  )
}

// Revokes the active Microsoft OAuth credential for ONE connector (ext_teams /
// ext_outlook) — disconnecting one must not tear down the other (LIN-1155).
// Omitting the connector revokes EVERY Microsoft credential (the pre-split
// behaviour).
export const disconnectMicrosoftOauth = (connector?: MicrosoftConnector) => async () => {
  return await responseWrapper<{ data: MicrosoftOauthStatus }>(() =>
    API.delete<{ data: MicrosoftOauthStatus }>(`${alphaPath}/oauth/microsoft`, {
      params: connector ? { connector } : undefined,
    })
  )
}

// --- Alert Agent (Trigger Layer) ---

export const createTrigger = (data: TriggerCreateParams) => async () => {
  return await responseWrapper<{ data: Trigger }>(() =>
    API.post<{ data: Trigger }>(`${alphaPath}/triggers`, data)
  )
}

export const updateTrigger = () => async (params: TriggerUpdateParams) => {
  const { id, ...body } = params
  return await responseWrapper<{ data: Trigger }>(() =>
    API.patch<{ data: Trigger }>(`${alphaPath}/triggers/${id}`, body)
  )
}

export const deleteTrigger = () => async (id: string) => {
  return await responseWrapper<{ success: boolean }>(() =>
    API.delete<{ success: boolean }>(`${alphaPath}/triggers/${id}`)
  )
}

// Skill Groups — main-page folders (PRD: linq-alpha-client#3680)

export const createSkillGroup = () => async (params: CreateSkillGroupParams) => {
  return await responseWrapper<SkillGroupResponse>(() =>
    API.post<SkillGroupResponse>(`${alphaPath}/skill_groups`, params)
  )
}

export const updateSkillGroup = () => async (params: UpdateSkillGroupParams) => {
  const { id, ...updateData } = params
  return await responseWrapper<SkillGroupResponse>(() =>
    API.patch<SkillGroupResponse>(`${alphaPath}/skill_groups/${id}`, updateData)
  )
}

export const deleteSkillGroup = () => async (id: string) => {
  return await responseWrapper<void>(() => API.delete<void>(`${alphaPath}/skill_groups/${id}`))
}

export const addSkillsToGroup =
  () =>
  async ({ id, workspace_skill_ids }: SkillGroupSkillsParams) => {
    return await responseWrapper<SkillGroupResponse>(() =>
      API.post<SkillGroupResponse>(`${alphaPath}/skill_groups/${id}/add_skills`, {
        workspace_skill_ids,
      })
    )
  }

export const removeSkillsFromGroup =
  () =>
  async ({ id, workspace_skill_ids }: SkillGroupSkillsParams) => {
    return await responseWrapper<SkillGroupResponse>(() =>
      API.delete<SkillGroupResponse>(`${alphaPath}/skill_groups/${id}/remove_skills`, {
        data: { workspace_skill_ids },
      })
    )
  }

// Reorder the skills INSIDE one folder (PRD COV-96 FR-G). The server renumbers every item,
// appending anything the caller omitted, so a partial list is safe.
export const reorderSkillsInGroup =
  () =>
  async ({ id, workspace_skill_ids }: SkillGroupSkillsParams) => {
    return await responseWrapper<SkillGroupResponse>(() =>
      API.post<SkillGroupResponse>(`${alphaPath}/skill_groups/${id}/reorder_skills`, {
        workspace_skill_ids,
      })
    )
  }

export const reorderSkillGroups = () => async (group_ids: string[]) => {
  return await responseWrapper<SkillGroupsResponse>(() =>
    API.post<SkillGroupsResponse>(`${alphaPath}/skill_groups/reorder`, { group_ids })
  )
}

// --- Ontology Entities (linq-alpha-server #3340) ---
// Writes require the ontology write role (403); locked entities reject update/delete (422).

export const createOntologyEntity = () => async (params: CreateOntologyEntityParams) => {
  return await responseWrapper<OntologyEntity>(() =>
    API.post<OntologyEntity>(`${alphaPath}/ontology_entities`, params)
  )
}

export const updateOntologyEntity = () => async (params: UpdateOntologyEntityParams) => {
  const { id, ...updateData } = params
  return await responseWrapper<OntologyEntity>(() =>
    API.patch<OntologyEntity>(`${alphaPath}/ontology_entities/${id}`, updateData)
  )
}

export const deleteOntologyEntity = () => async (id: string) => {
  return await responseWrapper<void>(() => API.delete<void>(`${alphaPath}/ontology_entities/${id}`))
}

// --- Web Monitor (Website Watch) ---

export const createWebWatch = (params: WebWatchCreateParams) => async () => {
  return await responseWrapper<WebWatchMutationResponse>(() =>
    API.post<WebWatchMutationResponse>(`${alphaPath}/web_watches`, params)
  )
}

export const updateWebWatch = () => async (params: WebWatchUpdateParams) => {
  const { id, ...body } = params
  return await responseWrapper<WebWatchMutationResponse>(() =>
    API.patch<WebWatchMutationResponse>(`${alphaPath}/web_watches/${id}`, body)
  )
}

// PATCH, not POST: the server routes pause/resume as member PATCH actions and
// returns the updated watch in the same envelope as create/update.
export const pauseWebWatch = () => async (id: string) => {
  return await responseWrapper<WebWatchMutationResponse>(() =>
    API.patch<WebWatchMutationResponse>(`${alphaPath}/web_watches/${id}/pause`)
  )
}

export const resumeWebWatch = () => async (id: string) => {
  return await responseWrapper<WebWatchMutationResponse>(() =>
    API.patch<WebWatchMutationResponse>(`${alphaPath}/web_watches/${id}/resume`)
  )
}

export const deleteWebWatch = () => async (id: string) => {
  return await responseWrapper<WebWatchDeleteResponse>(() =>
    API.delete<WebWatchDeleteResponse>(`${alphaPath}/web_watches/${id}`)
  )
}

// LIN-1056 — Slack connector. Returns the Slack authorize URL + opaque state;
// the client redirects the user to it, then hands the returned `?code=&state=`
// back via completeSlackOauthCallback.
export const startSlackOauth = () => async (params: SlackOauthStartParams) => {
  return await responseWrapper<{ data: SlackOauthStartResponse }>(() =>
    API.post<{ data: SlackOauthStartResponse }>(`${alphaPath}/oauth/slack/start`, params)
  )
}

// LIN-1573 — starts the workspace install. Same shape as the connector start
// above; the server decides the bot scopes, the client only says where Slack
// should come back to.
export const startSlackInstall = () => async (params: SlackInstallStartParams) => {
  return await responseWrapper<{ data: SlackOauthStartResponse }>(() =>
    API.post<{ data: SlackOauthStartResponse }>(`${alphaPath}/oauth/slack/install/start`, params)
  )
}

// Revokes the active Slack OAuth credential. alpha-server leaves the disconnect
// endpoint open to users without the connector role so a role-removed user can
// still clear a lingering credential.
export const disconnectSlackOauth = () => async () => {
  return await responseWrapper<{ data: SlackOauthStatus }>(() =>
    API.delete<{ data: SlackOauthStatus }>(`${alphaPath}/oauth/slack`)
  )
}

export const pinArtifact = (artifactId: string) => async () =>
  responseWrapper<{ artifact_id: string; is_pinned: boolean }>(
    () => API.put(`${alphaPath}/artifacts/${artifactId}/pin`),
    false
  )

export const unpinArtifact = (artifactId: string) => async () =>
  responseWrapper<{ artifact_id: string; is_pinned: boolean }>(
    () => API.delete(`${alphaPath}/artifacts/${artifactId}/pin`),
    false
  )

export const renameArtifact = (artifactId: string, displayName: string | null) => async () =>
  responseWrapper<ArtifactLibraryItem>(
    () =>
      API.patch(`${alphaPath}/artifacts/${artifactId}`, {
        display_name: displayName?.trim() || null,
      }),
    false
  )
